/**
 * FuelLevelSlider — horizontal fuel gauge 0-100% with visual fill bar.
 *
 * Props: value (0-100), onChange, step (default 5).
 * Shows percentage label and animated fill.
 */
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Pressable } from '@/components/ui/Pressable'
import { selection } from '@/lib/utils/haptics'
import { Fuel, Minus, Plus } from '@/components/ui/icons'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'

interface FuelLevelSliderProps {
  value: number
  onChange: (value: number) => void
  step?: number
  testIDPrefix?: string
}

export function FuelLevelSlider({
  value,
  onChange,
  step = 5,
  testIDPrefix = 'checkin',
}: FuelLevelSliderProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const clamp = (v: number) => Math.max(0, Math.min(100, v))
  const fillPct = `${value}%` as `${number}%`

  const fillColor = value > 50 ? colors.success : value > 25 ? colors.warning : colors.error

  const decrement = () => {
    const next = clamp(value - step)
    if (next !== value) {
      void selection()
      onChange(next)
    }
  }

  const increment = () => {
    const next = clamp(value + step)
    if (next !== value) {
      void selection()
      onChange(next)
    }
  }

  return (
    <View style={{ gap: SPACING.sm }}>
      {/* Label row */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
        <Fuel size={18} color={colors.textSecondary} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
          {value}%
        </Text>
      </View>

      {/* Controls row */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <Pressable
          onPress={decrement}
          accessibilityRole="button"
          accessibilityLabel={t('checkin.fuel_decrease')}
          testID={`${testIDPrefix}.fuel-decrease`}
          style={{
            width: 44,
            height: 44,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.surfaceSubtle,
            borderRadius: RADIUS.full,
          }}
        >
          <Minus size={20} color={colors.textPrimary} />
        </Pressable>

        {/* Gauge bar */}
        <View
          style={{
            flex: 1,
            height: 20,
            backgroundColor: colors.surfaceSubtle,
            borderRadius: RADIUS.full,
            overflow: 'hidden',
          }}
        >
          <View
            style={{
              width: fillPct,
              height: '100%',
              backgroundColor: fillColor,
              borderRadius: RADIUS.full,
            }}
          />
        </View>

        <Pressable
          onPress={increment}
          accessibilityRole="button"
          accessibilityLabel={t('checkin.fuel_increase')}
          testID={`${testIDPrefix}.fuel-increase`}
          style={{
            width: 44,
            height: 44,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.surfaceSubtle,
            borderRadius: RADIUS.full,
          }}
        >
          <Plus size={20} color={colors.textPrimary} />
        </Pressable>
      </View>
    </View>
  )
}
